import { memo, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { AppBar, Box, IconButton, Menu, MenuItem, Toolbar, Typography, Tooltip } from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit'
import { useNavigate } from 'react-router-dom'
import { IconHelp, IconMenu, IconResetPassword, IconLock } from '../assets/assets'
import Date from './Date'

const menuItems = [
  { to: '/', src: IconMenu, alt: 'Menu', label: 'Home' },
  {
    to: 'https://www.eapharmics.com/eahelp/',
    src: IconHelp,
    alt: 'Help',
    label: 'Help',
    newTab: true
  },
  { to: '/resetPassword', src: IconResetPassword, alt: 'Reset Password', label: 'Reset Password' },
  { to: '/login', src: IconLock, alt: 'Lock', label: 'Lock' }
  // { to: '/exit', src: IconExit, alt: 'Exit', label: 'Exit' }
]

const AppBarComponent = memo(({ username, title }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [isFullscreen, setIsFullscreen] = useState(!!document.fullscreenElement)
  const navigate = useNavigate()

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(err => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`)
      })
    } else {
      document.exitFullscreen()
    }
  }

  const handleMenuClick = event => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleMenuItemClick = item => {
    item.newTab ? window.open(item.to, '_blank') : navigate(item.to)
    handleMenuClose()
  }

  return (
    <AppBar 
      position="static" 
      elevation={0}
      sx={{ 
        backgroundColor: 'primary.main',
        borderRadius: 1,
        mb: 2,
        mt: 1
      }}
    >
      <Toolbar sx={{ justifyContent: 'center', position: 'relative' }}>
        <Box sx={{ position: 'absolute', left: 16, display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ color: 'white', opacity: 0.9 }}>
            <Date />
            <Box component="span" sx={{ ml: 1 }}>
              {username ? `Welcome ${username}` : 'Welcome'}
            </Box>
          </Typography>
        </Box>

        <Typography 
          variant="h3" 
          component="h1" 
          sx={{ 
            textAlign: 'center', 
            fontWeight: 600,
            color: 'white'
          }}
        >
          {title}
        </Typography>

        <Box sx={{ position: 'absolute', right: 16, display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title="Home Dashboard">
            <IconButton color="inherit" onClick={() => navigate('/')}>
              <Box component="img" src={IconMenu} alt="Home" sx={{ width: 24, height: 24 }} />
            </IconButton>
          </Tooltip>
          
          <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}>
            <IconButton color="inherit" onClick={toggleFullscreen}>
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>

          <IconButton 
            edge="end" 
            color="inherit" 
            aria-label="menu" 
            onClick={handleMenuClick}
          >
            <MenuIcon />
          </IconButton>
          
          <Menu 
            anchorEl={anchorEl} 
            open={Boolean(anchorEl)} 
            onClose={handleMenuClose}
            PaperProps={{
              sx: {
                minWidth: 180,
                boxShadow: 3
              }
            }}
          >
            {menuItems.map((item, index) => (
              <MenuItem 
                key={index} 
                onClick={() => handleMenuItemClick(item)}
              >
                <Box component="img" src={item.src} alt={item.alt} sx={{ width: 20, height: 20, mr: 1.5 }} />
                <Typography variant="body1">{item.label}</Typography>
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  )
})

AppBarComponent.propTypes = {
  username: PropTypes.string,
  title: PropTypes.string.isRequired
}

AppBarComponent.displayName = 'AppBarComponent'

export default AppBarComponent
